import { Link } from "@tanstack/react-router";
import { Col, Input, Row } from "antd";
import { useTenantLoginMutation } from "api/Auth/hooks.ts";
import queryClient from "api/queryClient.ts";
import { AuthAlert } from "components/UI/AuthAlert/AuthAlert.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { TenantAuthFormWrapper } from "components/Wrappers/TenantAuthFormWrapper/TenantAuthFormWrapper";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser.ts";
import { FC, useState } from "react";
import { FormItem } from "react-hook-form-antd";
import { extractValidationErrorMessages } from "utils/extractValidationErrorMessages.ts";
import { z } from "zod";

interface AffiliateLoginFormProps {
    tenantSlug?: string;
}

interface AffiliateLoginFormContentProps extends AffiliateLoginFormProps {
    submitError?: string | null;
    isSubmitting?: boolean;
}

const schema = z.object({
    email: z.string().min(1, "forms.general-validation.email_required").email("forms.general-validation.email_invalid"),
    password: z.string().min(1, "forms.general-validation.password_required"),
});

type AffiliateLoginFormData = z.infer<typeof schema>;

export const AffiliateLoginFormContent: FC<AffiliateLoginFormContentProps> = ({
    tenantSlug,
    submitError,
    isSubmitting,
}) => {
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tCommon } = usePaldockTranslation("common");
    const { t: tAuth } = usePaldockTranslation("auth.login");
    const generalFormUtils = useGeneralForm<AffiliateLoginFormData>();
    const { control, fieldNames } = generalFormUtils;

    const forgotPasswordLink = tenantSlug ? (
        <Link
            to="/$tenantSlug/auth/forgot-password"
            params={{ tenantSlug }}
            className="text-md text-black underline hover:no-underline hover:text-black"
        >
            {tAuth("links.forgot_password")}
        </Link>
    ) : (
        <Link to="/auth/forgot-password" className="text-md text-black underline hover:no-underline hover:text-black">
            {tAuth("links.forgot_password")}
        </Link>
    );

    return (
        <TenantAuthFormWrapper
            buttonText={tCommon("login")}
            loading={isSubmitting}
            buttonDisabled={isSubmitting}
            beforeButton={<div className="flex justify-end mt-1 mb-1">{forgotPasswordLink}</div>}
        >
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.email.name}
                        label={tFields("email.label")}
                        required
                        className="!mb-0"
                    >
                        <Input placeholder={tFields("email.placeholder")} />
                    </FormItem>
                </Col>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.password.name}
                        label={tFields("password.label")}
                        required
                        className="!mb-0"
                    >
                        <Input.Password placeholder={tFields("password.placeholder")} />
                    </FormItem>
                </Col>
                {submitError && (
                    <Col span={24}>
                        <AuthAlert type="error" message={submitError} />
                    </Col>
                )}
            </Row>
        </TenantAuthFormWrapper>
    );
};

export const AffiliateLoginForm: FC<AffiliateLoginFormProps> = ({ tenantSlug }) => {
    const { t: tAuth } = usePaldockTranslation("auth.login");
    const userUtils = useUser();
    const [submitError, setSubmitError] = useState<string | null>(null);
    // The login mutation settles before the post-login redirect (which awaits its own "/me" fetch)
    // finishes — without this, the submit button flashes back to enabled for that gap.
    const [isRedirecting, setIsRedirecting] = useState(false);

    const tenantLoginMutation = useTenantLoginMutation(
        tenantSlug ?? "",
        {
            onSuccess: (response) => {
                setIsRedirecting(true);
                queryClient.removeQueries({ queryKey: ["me"] });
                userUtils.loginUser(response?.token || "", response.user, tenantSlug);
            },
            onError: (error) => {
                setSubmitError(extractValidationErrorMessages(error)[0] ?? tAuth("messages.invalid_credentials"));
            },
        },
        true
    );

    const defaultValues = {
        email: "",
        password: "",
    };

    const onSubmit = (data: AffiliateLoginFormData) => {
        setSubmitError(null);
        if (tenantSlug) {
            tenantLoginMutation.mutate(data);
        } else {
            userUtils.authenticateUser(data, tenantSlug);
        }
    };

    return (
        <GeneralFormProvider<AffiliateLoginFormData>
            schema={schema}
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            requiredMark={false}
        >
            <AffiliateLoginFormContent
                tenantSlug={tenantSlug}
                submitError={submitError}
                isSubmitting={tenantLoginMutation.isPending || isRedirecting}
            />
        </GeneralFormProvider>
    );
};
